"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import type { ChatStatus } from "ai";
import { SquareIcon } from "lucide-react";
import { type ReactNode, useId } from "react";
import { PromptInputButton } from "@/components/ai-elements/prompt-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAiChatUiContext } from "@/lib/ai/ai-chat-ui-context";
import { type AiModelAccess } from "@/lib/ai/model-access";
import { ModelId } from "@/lib/ai/models";
import type { AnalysisTargetOptionsState } from "./analysis-target-options";
import {
  ChatPromptSurface,
  ChatPromptSurfaceInput,
} from "./chat-prompt-surface";
import type { ChatQuotaTierIndicatorState } from "./chat-quota-view-state";
import type { ChatSendInput } from "./chat-send-input";

const HIDDEN_QUOTA_INDICATOR: ChatQuotaTierIndicatorState = {
  kind: "hidden",
};

type Props = {
  status: ChatStatus;
  onSend: (message: ChatSendInput) => Promise<boolean | void> | boolean | void;
  onAbort: () => void;
  isEditable?: boolean;
  model: ModelId;
  aiModelAccess: AiModelAccess;
  onModelChange: (model: ModelId) => void;
  isCurrentModelQuotaExceeded?: boolean;
  statusCard?: ReactNode;
  lowQuotaIndicator?: ChatQuotaTierIndicatorState;
  highQuotaIndicator?: ChatQuotaTierIndicatorState;
  pathname: string;
  optionsState: AnalysisTargetOptionsState;
  suppressPageContextAutoSyncOnCurrentPath: () => void;
  showDisclaimer?: boolean;
  inputHeader?: ReactNode;
};

/**
 * 既存スレッドで使うメインのチャット入力 UI。
 */
export function ChatComposer({
  onSend,
  onAbort,
  status,
  isEditable,
  model,
  aiModelAccess,
  onModelChange,
  isCurrentModelQuotaExceeded = false,
  statusCard,
  lowQuotaIndicator = HIDDEN_QUOTA_INDICATOR,
  highQuotaIndicator = HIDDEN_QUOTA_INDICATOR,
  pathname,
  optionsState,
  suppressPageContextAutoSyncOnCurrentPath,
  showDisclaimer = true,
  inputHeader,
}: Props) {
  const { t } = useLingui();
  const autoApproveSwitchId = useId();
  const { autoApproveToolConfirmations, setAutoApproveToolConfirmations } =
    useAiChatUiContext();
  const editable = isEditable !== false;
  const isInteractive = editable && (status === "ready" || status === "error");

  return (
    <ChatPromptSurface
      onSubmit={onSend}
      model={model}
      aiModelAccess={aiModelAccess}
      onModelChange={onModelChange}
      pathname={pathname}
      optionsState={optionsState}
      suppressPageContextAutoSyncOnCurrentPath={
        suppressPageContextAutoSyncOnCurrentPath
      }
      isInteractive={isInteractive}
      submitBlocked={isCurrentModelQuotaExceeded}
      keepExpanded={status === "streaming"}
      lowQuotaIndicator={lowQuotaIndicator}
      highQuotaIndicator={highQuotaIndicator}
      restoreFocusAfterSubmit
      className="space-y-1.5 transition-[margin] duration-200 ease-out"
      compactClassName="space-y-1.5"
    >
      {statusCard}
      <ChatPromptSurfaceInput
        showDisclaimer={showDisclaimer && !statusCard}
        floatingDisclaimer
        disclaimerPlacement="before-input"
        inputHeader={inputHeader}
        placeholder={
          editable
            ? t`Enter your message`
            : t`Only the creator can send messages`
        }
        bodyClassName="bg-background"
        expandedTextareaClassName="min-h-10 py-2 text-sm leading-5"
        compactTextareaClassName="text-sm leading-5"
        expandedToolbarClassName="px-2.5 pb-2 pt-1.5"
        toolbarEnd={
          <>
            <div className="flex items-center gap-1.5 border-r border-border/60 pr-2 sm:gap-2">
              <Switch
                // PromptInput は送信時に form を reset し、Radix Switch は mount 時の値へ戻す。
                // 保存済み設定が変わった時だけ再 mount して、reset の基準値を最新に保つ。
                key={autoApproveToolConfirmations ? "enabled" : "disabled"}
                id={autoApproveSwitchId}
                checked={autoApproveToolConfirmations}
                onCheckedChange={setAutoApproveToolConfirmations}
                disabled={!isInteractive}
              />
              <Label
                htmlFor={autoApproveSwitchId}
                className="cursor-pointer text-xs font-normal text-muted-foreground max-sm:sr-only"
              >
                <Trans>Auto-approve non-destructive actions</Trans>
              </Label>
            </div>
            {status === "streaming" ? (
              <PromptInputButton
                type="button"
                onClick={onAbort}
                variant="outline"
                className="rounded-full"
                title={t`Stop`}
                aria-label={t`Stop`}
              >
                <SquareIcon className="h-4 w-4" />
              </PromptInputButton>
            ) : null}
          </>
        }
      />
    </ChatPromptSurface>
  );
}
